// Browser Notifications Hook
import { useEffect, useCallback, useRef } from 'react';

// Check if Notification API is supported (iOS Safari doesn't support it)
const isNotificationSupported = () => {
  return typeof window !== 'undefined' && 'Notification' in window;
};

const getNotificationPermission = () => {
  if (!isNotificationSupported()) return 'denied';
  try {
    return Notification.permission;
  } catch (e) {
    return 'denied';
  }
};

export const useNotifications = () => {
  const permission = useRef(getNotificationPermission());

  const requestPermission = useCallback(async () => {
    if (!isNotificationSupported()) {
      console.log('This browser does not support notifications');
      return false;
    }

    try {
      if (Notification.permission === 'granted') {
        permission.current = 'granted';
        return true;
      }

      if (Notification.permission !== 'denied') {
        const result = await Notification.requestPermission();
        permission.current = result;
        return result === 'granted';
      }
    } catch (e) {
      console.log('Notification permission request failed:', e);
      return false;
    }

    return false;
  }, []);

  const sendNotification = useCallback((title, options = {}) => {
    if (!isNotificationSupported() || permission.current !== 'granted') {
      return null;
    }

    try {
      const notification = new Notification(title, {
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        vibrate: [200, 100, 200],
        ...options,
      });

      notification.onclick = () => {
        window.focus();
        notification.close();
        if (options.onClick) {
          options.onClick();
        }
      };

      return notification;
    } catch (e) {
      console.log('Failed to create notification:', e);
      return null;
    }
  }, []);

  const notifyNewSignal = useCallback((signal) => {
    const isLong = signal.bias?.toLowerCase().includes('long') || 
                   signal.bias?.toLowerCase().includes('bullish');
    const isShort = signal.bias?.toLowerCase().includes('short') || 
                    signal.bias?.toLowerCase().includes('bearish');
    
    const direction = isLong ? '🟢 LONG' : isShort ? '🔴 SHORT' : '⚪ NEUTRAL';
    const premium = signal.Premium ? '⭐ Premium' : '';
    
    return sendNotification(`New Signal: ${signal.symbol}`, {
      body: `${direction} ${premium}\n${signal.title || 'New trading opportunity available'}`,
      tag: `signal-${signal._id || signal.id}`,
      requireInteraction: true,
      data: { signalId: signal._id || signal.id },
    });
  }, [sendNotification]);

  const isSupported = isNotificationSupported();
  const isGranted = permission.current === 'granted';

  return {
    isSupported,
    isGranted,
    permission: permission.current,
    requestPermission,
    sendNotification,
    notifyNewSignal,
  };
};

// Service for checking new signals
export class SignalNotificationService {
  constructor(apiUrl, token) {
    this.apiUrl = apiUrl;
    this.token = token;
    this.lastChecked = new Date();
    this.knownSignalIds = new Set();
    this.checkInterval = null;
    this.onNewSignal = null;
  }

  setToken(token) {
    this.token = token;
  }

  setOnNewSignal(callback) {
    this.onNewSignal = callback;
  }

  async fetchSignals() {
    try {
      const response = await fetch(`${this.apiUrl}/api/trading_setups`, {
        headers: {
          'Authorization': `Bearer ${this.token}`,
        },
      });
      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      console.error('Failed to fetch signals for notifications:', error);
    }
    return [];
  }

  async checkForNewSignals() {
    const signals = await this.fetchSignals();
    const newSignals = [];

    for (const signal of signals) {
      const signalId = signal._id || signal.id;
      if (!this.knownSignalIds.has(signalId)) {
        // Check if signal was created after we started watching
        const createdAt = new Date(signal.created_at);
        if (createdAt > this.lastChecked) {
          newSignals.push(signal);
        }
        this.knownSignalIds.add(signalId);
      }
    }

    if (newSignals.length > 0 && this.onNewSignal) {
      for (const signal of newSignals) {
        this.onNewSignal(signal);
      }
    }

    return newSignals;
  }

  start(intervalMs = 30000) {
    // Initialize known signals
    this.fetchSignals().then(signals => {
      signals.forEach(signal => {
        this.knownSignalIds.add(signal._id || signal.id);
      });
      this.lastChecked = new Date();
    });

    // Start polling
    this.checkInterval = setInterval(() => {
      this.checkForNewSignals();
    }, intervalMs);
  }

  stop() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }
}

export default useNotifications;
